// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import {
	AmountByLoan,
	ErrorCause,
	ExchangeRateByDate,
	Feedback,
	LoadingStatus,
	Loan,
	PaymentPayload,
	Transaction,
	TransactionMetadata,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextUserProjectPermissions } from '../../pages/Project/Project.context';

// hooks
import { useExchangeRates } from '../../../hooks/useExchangeRates';

// fields
import { Amounts } from '../../fields/Amounts/Amounts';
import { ExchangeRates } from '../../fields/ExchangeRates/ExchangeRates';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// mui
import { Grid } from '@mui/material';

// utils
import { translateError } from '../../../utils/translations';
import { formatDate } from '../../../utils/dates';

// props
export interface FormAddPaymentRefundProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setStatus: Dispatch<SetStateAction<LoadingStatus>>;
	status: LoadingStatus;
	transaction: Transaction;
}

const formId = 'form-add-payment-refund';

export interface ValuesProps {
	amounts: AmountByLoan[];
	date: string;
	files: File[];
}

export const FormAddPaymentRefund = ({
	onClose,
	onSuccess,
	setStatus,
	transaction,
}: FormAddPaymentRefundProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const { t } = useTranslation();
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { userProjectPermissions } = useContext(ContextUserProjectPermissions);

	// state
	const [exchangeRates, setExchangeRates] = useState<ExchangeRateByDate[]>(() => {
		const currency = transaction.currency || '';
		const currencies = getCurrenciesForExchangeRates({ currency });
		return currencies.map((c) => ({
			currency: c,
			dateExchangeRate: '',
			exchangeRate: 0,
		}));
	});
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [values, setValues] = useState<ValuesProps>({
		amounts: [],
		date: formatDate({ date: new Date(), format: 'YYYY-MM-DD' }),
		files: [],
	});

	// vars
	const currency = transaction.currency || '';
	const fromAccount = typeof transaction.fromAccount === 'object' ? transaction.fromAccount : null;
	const fromEntity = typeof transaction.fromEntity === 'object' ? transaction.fromEntity : null;
	const loans: Loan[] = transaction.payments
		.map((p) => p.loan)
		.filter((l): l is Loan => typeof l === 'object' && l !== null);
	const toEntity = typeof transaction.toEntity === 'object' ? transaction.toEntity : null;

	const handleChangeAmounts = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		_total: number,
		loan: Loan
	) => {
		const amountsNew = [...values.amounts];
		const amount = Number(e.target.value);
		const amountByLoan = amountsNew.find((a) => a.loan === loan.id);

		if (amountByLoan) {
			amountByLoan.amount = amount;
		} else {
			amountsNew.push({ amount, loan: loan.id, tax: 0 });
		}

		setValues((prevState) => ({ ...prevState, amounts: amountsNew }));
	};

	const handleChangeExchangeRates = (index: number, name: string, value: string | number) => {
		const exchangeRatesNew = [...exchangeRates];
		exchangeRatesNew[index] = {
			...exchangeRatesNew[index],
			[name]: value,
		};
		setExchangeRates(exchangeRatesNew);
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${formId}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		const payments: PaymentPayload[] = [];
		let total = 0;

		for (const amountByLoan of values.amounts) {
			if (amountByLoan.amount <= 0) continue;

			const loan = loans.find((l) => l.id === amountByLoan.loan);
			if (!loan) continue;

			const conversions = exchangeRates.map((exchangeRate, indexExchangeRate) => {
				const conversionAmount =
					Number(entries[`source-${loans.indexOf(loan)}-amount-conversion-${indexExchangeRate}`]) ||
					0;
				return {
					amount: conversionAmount,
					currency: exchangeRate.currency,
					dateExchangeRate: exchangeRate.dateExchangeRate,
					exchangeRate: exchangeRate.exchangeRate,
					tax: 0,
				};
			});

			total += amountByLoan.amount;

			payments.push({
				amount: amountByLoan.amount,
				conversions: conversions.length > 0 ? conversions : undefined,
				currency,
				loan: loan.id,
			});
		}

		const metadata: TransactionMetadata = {
			uuid: crypto.randomUUID(),
		};

		try {
			setFeedback(null);
			setStatus('LOADING');

			if (!total || total === 0) {
				throw new Error('The payment amount needs to be greater than 0.', {
					cause: {
						id: 'TRANSACTION_AMOUNT_NON_EXISTENT',
					},
				});
			}

			const projectId =
				typeof transaction.project === 'object' ? transaction.project.id : transaction.project;

			const body = {
				date: formatDate({ date: values.date, format: 'YYYY-MM-DD' }),
				metadata,
				payments,
				project: projectId,
				transaction: transaction.id,
				type: 'PAYMENT_REFUND',
			};

			const fetchAddRefund = await fetch(`${process.env.REACT_APP_API_URL}/transactions`, {
				method: 'post',
				body: JSON.stringify(body),
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			});
			const resAddRefund = await fetchAddRefund.json();

			if (resAddRefund.error) {
				throw new Error(resAddRefund.error.message, {
					cause: {
						details: resAddRefund.error.details,
						id: resAddRefund.error.id,
					},
				});
			}

			const transactionRefund = resAddRefund.data;

			// add documents using files
			for (let i = 0; i < values.files.length; i++) {
				const formData = new FormData();
				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				formData.append(
					'path',
					`bldt/projects/${projectId}/refunds/${transactionRefund.id}/${year}/${month}/`
				);
				formData.append('file', values.files[i]);
				formData.append('project', String(projectId));
				formData.append('transaction', String(transactionRefund.id));

				let fetchPostDoc: Response;

				try {
					fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
						method: 'post',
						body: formData,
						headers: {
							Authorization: `Bearer ${accessToken}`,
							User: String(idToken),
						},
					});
				} catch {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				if (fetchPostDoc.status === 403) {
					throw new Error('Forbidden', { cause: { id: 'DOCUMENT_FORBIDDEN' } });
				}

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					feedbackNew.items.push({
						alert: {
							children: translateError({
								cause: {
									...resPostDoc.error,
								},
								t,
							}),
							severity: 'error',
						},
						response: resPostDoc,
					});
				}
			}

			// post comment if entered
			if (entries.comments) {
				const fetchPostComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
					method: 'post',
					body: JSON.stringify({
						comment: entries.comments,
						transaction: transactionRefund.id,
					}),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostComment = await fetchPostComment.json();

				if (resPostComment.error) {
					feedbackNew.items.push({
						alert: {
							children: translateError({
								cause: {
									...resPostComment.error,
								},
								t,
							}),
							severity: 'error',
						},
						response: resPostComment,
					});
				}
			}

			feedbackNew.items.push({
				alert: {
					children: t('alert.refundRecorded'),
					severity: 'success',
				},
			});

			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				if (onSuccess) {
					onSuccess();
				}

				setStatus('SUCCESS');

				// set success banner
				setBanner({
					hasClose: true,
					message: t('alert.paymentRefundCreated', { name: entries.name }),
					severity: 'success',
				});

				if (onClose) {
					onClose();
				}
			} else {
				setFeedback(feedbackNew);
				setStatus('ERROR');
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});

			setFeedback(feedbackNew);
			setStatus('ERROR');
		}
	};

	return (
		<form id={formId} name="formAddRefund" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				{toEntity && (
					<Grid item xs={12} md={6}>
						<Input
							id={`${formId}-supplier`}
							InputProps={{ readOnly: true }}
							label={t('supplier')}
							name="fromEntity"
							value={toEntity.name}
						/>
					</Grid>
				)}
				{fromEntity && (
					<Grid item xs={12} md={6}>
						<Input
							id={`${formId}-entity`}
							InputProps={{ readOnly: true }}
							label={t('entity')}
							name="toEntity"
							value={fromEntity.name}
						/>
					</Grid>
				)}
				{fromAccount && (
					<Grid item xs={12}>
						<Input
							id={`${formId}-receiving-account`}
							InputProps={{ readOnly: true }}
							label={t('receivingAccount')}
							name="toAccount"
							value={fromAccount.name}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<Input
						id={`${formId}-date`}
						inputProps={{
							max: formatDate({ date: new Date(), format: 'YYYY-MM-DD' }),
						}}
						label={t('date')}
						name="date"
						onChange={(e) => setValues((prev) => ({ ...prev, date: e.target.value }))}
						required={true}
						type="date"
						value={values.date}
					/>
				</Grid>
				{exchangeRates.length > 0 && currency && (
					<Grid item xs={12}>
						<ExchangeRates
							currencyBase={currency}
							exchangeRates={exchangeRates}
							onChange={handleChangeExchangeRates}
						/>
					</Grid>
				)}
				{loans.length > 0 && (
					<Grid item xs={12}>
						<Amounts
							currency={currency}
							exchangeRates={exchangeRates}
							formId={formId}
							hasCurrency={false}
							hasTax={false}
							loans={loans}
							onChange={handleChangeAmounts}
							title={t('financingSources')}
							titleTotal={t('totalRefund')}
						/>
					</Grid>
				)}
				<Grid item xs={12}>
					<FilePicker
						onChange={(e, filesSelected) => {
							setValues((valuesPrev) => {
								return {
									...valuesPrev,
									files: filesSelected,
								};
							});
						}}
					/>
				</Grid>
				{userProjectPermissions?.comment?.create && (
					<Grid item xs={12}>
						<Input
							id={`${formId}-comments`}
							label={t('comments')}
							minRows={2}
							multiline={true}
							name="comments"
							required={true}
						/>
					</Grid>
				)}
				{feedback?.items?.length ? (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				) : null}
			</Grid>
		</form>
	);
};
